import { Search } from 'lucide-react'
import { Link } from 'react-router'
import { Kbd } from '@/components/ui/kbd'
import type { LogoParokiBaca } from '@/db/queries/types'
import { useWorkerRead } from '@/hooks/use-worker-read'
import { isKeuskupan, useSesi } from '../store/sesi'
import { SyncChip } from './SyncChip'
import { UserBlock } from './UserBlock'

interface HeaderProps {
  /** "Paroki Katedral Salib Suci Agats", or "Keuskupan Agats-Asmat" for diocese roles. */
  nama: string
  /** Nama pendek, shown under 768 px. */
  namaPendek: string
  /** Plan 10: "(Admin Keuskupan)" while an admin keuskupan works in this parish. */
  catatan?: string | null
  onCari(): void
}

/** The cathedral's colour mark: the header's mark without a parish logo, and the diocese's. */
export const MARK_KATEDRAL = '/brand/logo-katedral-baru.svg'

/**
 * The logo of the parish the session works in (spec 2026-10-03 logo-paroki, 5), read from
 * this device's `pengaturan_paroki` and read again after every save or pulled page, as the
 * header's name follows them; null: the cathedral's mark (no logo, no parish, or a diocese
 * view, the admin keuskupan's Keuskupan included, or a read that failed). An answer for another
 * parish than the one worked in now (a switch under way) is not shown. Undefined while the
 * first answer for the parish is on its way: not known yet, so the header shows neither.
 */
export function useLogoKepala(): string | null | undefined {
  const parokiId = useSesi((s) => (isKeuskupan(s.sesi.peran) ? null : s.sesi.parokiId))
  const { data, error } = useWorkerRead<LogoParokiBaca>(
    'pengaturan:logo',
    { parokiId },
    { enabled: parokiId !== null },
  )
  if (parokiId === null) return null
  if (data === undefined) return error === null ? undefined : null
  if (data === null) return null
  return data.parokiId === parokiId ? data.logo : null
}

/**
 * 48 px of paper with a hairline beneath: the colour mark, or the parish's own logo while
 * working in a parish that has one (decorative either way; the splash settles onto it, and its
 * place stays empty until the device has said which, so a launch never paints the cathedral's
 * mark and then swaps it for the parish's), the parish name (short form under 768 px), the
 * search field with its Ctrl K hint from 1024 px (an icon below, where a keyboard is rare), the
 * sync chip and the person (from 768 px). Held still through route changes by its
 * view-transition name.
 */
export function Header({ nama, namaPendek, catatan = null, onCari }: HeaderProps) {
  const logo = useLogoKepala()
  return (
    <header className="vt-header sticky top-0 z-20 flex h-12 items-center gap-2 border-b border-border bg-bg px-4 md:gap-4 md:px-6">
      <Link
        to="/"
        aria-label={catatan === null ? nama : `${nama} ${catatan}`}
        className="-ml-2 flex h-11 min-w-0 items-center gap-2 px-2 focus-settle md:shrink-0"
      >
        <img
          src={logo ?? MARK_KATEDRAL}
          alt=""
          aria-hidden="true"
          width={32}
          height={32}
          className={
            logo === undefined
              ? 'invisible size-8 shrink-0 object-contain'
              : 'size-8 shrink-0 object-contain'
          }
          data-mark-target=""
        />
        <span className="truncate text-label font-medium text-text">
          <span className="md:hidden">{namaPendek}</span>
          <span className="hidden md:inline">{nama}</span>
          {catatan === null ? null : <span className="font-normal text-muted"> {catatan}</span>}
        </span>
      </Link>

      <button
        type="button"
        onClick={onCari}
        aria-label="Cari"
        aria-keyshortcuts="Control+K"
        className="mx-auto hidden h-8 w-full min-w-0 max-w-[28rem] pointer-coarse:h-11 items-center gap-2 rounded-sm border border-ink-soft bg-bg px-3 text-label text-muted hover-band focus-settle lg:flex"
      >
        <Search size={16} aria-hidden="true" className="shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">Cari nama, nama baptis, NIK…</span>
        <Kbd aria-hidden="true">Ctrl K</Kbd>
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-1 md:gap-4">
        <button
          type="button"
          onClick={onCari}
          aria-label="Cari"
          aria-keyshortcuts="Control+K"
          className="flex size-11 items-center justify-center text-text hover-band focus-settle lg:hidden"
        >
          <Search size={20} strokeWidth={1.75} aria-hidden="true" />
        </button>
        {/* The worded chip needs the room the laptop header has; tablets get the compact one
            (the offline sentence plus the user block overflowed 768 px). */}
        <SyncChip compact className="lg:hidden" />
        <SyncChip className="hidden lg:inline-flex" />
        <UserBlock className="hidden md:flex" />
      </div>
    </header>
  )
}
